import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ThemeContext } from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import Loading from '../../../../_shared/components/Loading';
import Gallery from './Gallery';
import MultiMediaButton from './MultiMediaButton';
import MediaGallery from '../../MediaGallery';
import MixpanelConfig from '../../../../_shared/constants/mixpanelConfig';
import SegmentConfig from '../../../../_shared/constants/segmentConfig';
import useAnalytics, {
  getSellerProperties,
  getVinBasicProperties,
  getVinLaunchProperties
} from '../../../../_shared/hooks/useAnalytics';
import useTrackMultiMediaGallery from '../../../../_shared/hooks/useTrackMultiMediaGallery';

import {
  BigImageContainer,
  EmptyIcon,
  GradientPattern,
  Image,
  ImageContainer,
  StyledMultiMedia,
  Wrapper
} from './Styles';
import { TradeContext } from '../../../../_shared/types/trade';
import isOmpPlatform from '../../../../_shared/utils/isOmpPlatform';

const MultiMedia = ({
  mediaList,
  tradeDetails,
  tradeId,
  tradeContext,
  tradeState
}) => {
  const themeContext = useContext(ThemeContext);
  const isDesktop = useMediaQuery({
    query: `(min-width: ${themeContext.BP.VDP_DESKTOP_WL})`
  });
  const forceMiniVdp = themeContext.FORCE_MINI_VDP_OMP;
  const isSelling = tradeContext === TradeContext.Sell;
  const [isOverlayOpen, setOverlayOpen] = useState(false);
  const [selectedPhotoId, setSelectedPhotoId] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const track = useAnalytics();
  const trackMultiMediaGallery = useTrackMultiMediaGallery();

  useEffect(() => {
    if (mediaList?.photos?.length > 0 && tradeDetails?.id === tradeId) {
      setIsLoading(false);
    }
  }, [mediaList?.photos?.length, tradeDetails?.id, tradeId]);

  const openOverlay = () => {
    setOverlayOpen(true);
  };

  const closeOverlay = () => {
    setOverlayOpen(false);
  };

  const openMultiMediaOverlay = useCallback(
    photo => {
      const {
        id,
        externalUrl,
        isBackPassengerSide,
        imageLocation: { displayName }
      } = photo;

      if (!id || !externalUrl || isBackPassengerSide) {
        return;
      }

      setSelectedPhotoId(id);
      openOverlay();

      if (isOmpPlatform()) {
        track(SegmentConfig.names.vdpOverviewImageInteraction, {
          ...getSellerProperties(tradeDetails),
          ...getVinBasicProperties(tradeDetails),
          ...getVinLaunchProperties(tradeDetails)
        });
      } else {
        track(MixpanelConfig.names.vdpOverviewImageInteraction, {
          source: `${displayName}`
        });
      }

      trackMultiMediaGallery({
        tradeDetails,
        tradeState,
        mediaList,
        tradeContext,
        galleryOpenType: SegmentConfig.galleryOpenType.overviewImage
      });
    },
    [
      track,
      trackMultiMediaGallery,
      tradeDetails,
      tradeState,
      mediaList,
      tradeContext
    ]
  );

  const [imgErrors, setImgErrors] = useState([]);

  const handleImageError = useCallback(
    (e, index) => {
      if (!Number.isNaN(index) && !imgErrors.includes(index))
        setImgErrors(errors => errors.concat(index));
    },
    [imgErrors]
  );

  const DesktopGallery = useCallback(
    ({ photos }) => {
      const { photosMap } = photos?.meta;

      const ImageWrapper = ({ photo, index }) =>
        photo.previewUrl && !imgErrors.includes(index) ? (
          <Image
            src={photo.previewUrl}
            pointer={!!photo.previewUrl}
            onClick={() => {
              openMultiMediaOverlay(photo);
            }}
            onError={e => handleImageError(e, index)}
          />
        ) : (
          <EmptyIcon />
        );

      const galleryItems = [];
      photosMap.forEach((photo, index) => {
        if (index === 0) {
          galleryItems.push(
            <BigImageContainer
              onlyGlamourShot={isSelling}
              key={photo.mediaId}
              className="vdpMultimedia-container"
            >
              <ImageWrapper photo={photo} index={index} />
              {isSelling && (
                <>
                  <GradientPattern />
                  <MultiMediaButton
                    mediaList={mediaList}
                    tradeDetails={tradeDetails}
                    setOverlayOpen={setOverlayOpen}
                    inBigImageContainer
                    tradeContext={tradeContext}
                    tradeState={tradeState}
                  />
                </>
              )}
            </BigImageContainer>
          );
          return;
        }

        if (!isSelling) {
          galleryItems.push(
            <ImageContainer key={photo.mediaId}>
              <ImageWrapper photo={photo} index={index} />
            </ImageContainer>
          );
        }
      });

      return <>{galleryItems} </>;
    },
    [
      imgErrors,
      openMultiMediaOverlay,
      handleImageError,
      tradeDetails,
      mediaList,
      tradeState,
      tradeContext,
      isSelling
    ]
  );

  const MobileGallery = useCallback(
    ({ photos }) => {
      const { photosMap } = photos?.meta;

      const noDesktopGalleryPhotos = 2;
      const mobileGalleryItems = [];

      for (let i = 0; i < noDesktopGalleryPhotos; i += 1) {
        mobileGalleryItems.push(
          <ImageContainer key={i}>
            {photosMap[i].externalUrl && !imgErrors.includes(i) ? (
              <Image
                src={photosMap[i].externalUrl}
                pointer={!!photosMap[i].externalUrl}
                onClick={() => {
                  openMultiMediaOverlay(photosMap[i]);
                }}
                onError={handleImageError}
              />
            ) : (
              <EmptyIcon />
            )}
          </ImageContainer>
        );
      }

      return <>{mobileGalleryItems}</>;
    },
    [imgErrors, openMultiMediaOverlay, handleImageError]
  );

  return (
    <StyledMultiMedia isSelling={isSelling} data-testid="vdp-multimedia">
      {isLoading ? (
        <Loading height="100%" />
      ) : (
        <>
          <Wrapper id="mediaGallery" isSelling={isSelling}>
            {!isDesktop || forceMiniVdp ? (
              <MobileGallery photos={mediaList} />
            ) : (
              <DesktopGallery photos={mediaList} />
            )}
            {!isSelling && (
              <Gallery
                mediaList={mediaList}
                tradeDetails={tradeDetails}
                setOverlayOpen={setOverlayOpen}
              />
            )}
          </Wrapper>
          <MediaGallery
            isOpen={isOverlayOpen}
            closeOverlay={closeOverlay}
            mediaList={mediaList}
            tradeDetails={tradeDetails}
            preSelectedPhotoId={selectedPhotoId}
          />
        </>
      )}
    </StyledMultiMedia>
  );
};

export default MultiMedia;
